// Role-segmented dashboard views (Phase F.1).
//
// Every dashboard surface composes which sections to render from
// getRoleView(profile.role). Same data model, different lens — the
// franchisee sees their store(s), the manager sees their shift, the
// admin/super_admin sees the system.
//
// PR A scope: admin/super_admin view is the only one fully populated.
// Corporate / manager / employee views are scaffolded with declared
// sections so feature flags don't have to be sprinkled across the
// dashboard, but the deeper surfaces (per-shift drill-downs, employee
// task focus) ship in follow-up PRs. RLS continues to gate the
// underlying data regardless of which sections a role declares.
//
// New roles or new sections get added here; consumers (Dashboard.tsx,
// OperatorKpiTiles.tsx, PrimeCostHero.tsx) read the declared sections
// and render — they never branch on role directly.

export type AppRole =
  | 'super_admin'
  | 'admin'
  | 'corporate'
  | 'lead_manager'
  | 'manager'
  | 'employee';

/** Section keys a role view can opt into. Each one maps to a concrete
 *  component on the Dashboard surface. Keep this list aligned with the
 *  switch in DashboardSections.tsx (added in a follow-up commit). */
export type DashboardSection =
  | 'primeCostHero'      // Admin-only — the EOD projection hero + drill-down.
  | 'operatorKpiTiles'   // Legacy 3-tile strip (Prime / Food / Labor).
  | 'liveSalesTiles'     // Legacy sales/orders tiles.
  | 'locationCockpit'    // Single-location operations cockpit (corporate + admin).
  | 'fleetCommand'       // All-stores fleet command (admin/super_admin + corporate).
  | 'shiftCheckin'       // "Start your day" check-in prompt (feature-gated).
  | 'attentionInbox'     // Consolidated needs-attention inbox: temps + overdue + comms/maintenance.
  | 'taskList'           // Tasks assigned to / created by the user.
  | 'checklistHub'       // Daily checklist completion.
  | 'salesGoals'         // Weekly sales goals widget (feature-gated).
  | 'reviews'            // Google reviews widget.
  | 'coverageBoard'      // Shift swap / coverage requests.
  | 'teamPulse'          // Engagement stats strip (activity / posts / recognition).
  | 'alertInbox';        // Recent alert_fires.

export interface RoleView {
  /** Stable identifier for the view; matches the key in ROLE_VIEWS. */
  id: 'admin' | 'corporate' | 'lead_manager' | 'manager' | 'employee';
  /**
   * Internal label for this view. NOT rendered anywhere — consumers
   * (UnifiedDashboard, useDashboardPrefs) read only `dashboardSections` and
   * `defaultHiddenSections`. It is documentation for whoever edits this file,
   * so it deliberately does NOT go through role-labels.ts, and a company
   * rename neither does nor should change it. Anything user-facing must call
   * getRoleLabel() instead.
   */
  label: string;
  /** One-line description of the lens this view represents. */
  description: string;
  /** Which app_role values resolve to this view. */
  appliesTo: AppRole[];
  /** Sections to render on the main Dashboard surface, in order. */
  dashboardSections: DashboardSection[];
  /** Sections HIDDEN by default for this role ("quality over quantity" —
   *  each role leads with only what it needs). Users re-show any of
   *  these via the dashboard Customize menu; the preference is stored
   *  as a per-user delta in useDashboardPrefs, so a role's defaults can
   *  evolve without clobbering individual choices. Must be a subset of
   *  dashboardSections. */
  defaultHiddenSections: DashboardSection[];
  /** Default alert_thresholds metrics this role subscribes to at
   *  account creation. The Alert Settings UI lets admins toggle. */
  defaultAlertSubscriptions: string[];
  /** True when every section listed is wired to a real component in
   *  this PR. False means the view is scaffolded — the Dashboard
   *  falls through to the legacy layout for these roles until a
   *  later PR fleshes out the missing sections. */
  fullyImplemented: boolean;
}

export const ROLE_VIEWS: Record<RoleView['id'], RoleView> = {
  admin: {
    id: 'admin',
    label: 'Admin / Super Admin',
    description:
      'System-wide visibility with the prime cost hero, per-location drill-down, and the full alert inbox.',
    appliesTo: ['admin', 'super_admin'],
    // The All-Stores Command (fleetCommand) fleet KPI strip + exception
    // feed supersede the standalone operatorKpiTiles + alertInbox on the
    // dashboard page; the prime-cost hero (EOD projection) stays.
    //
    // 2026-07 declutter: sections below the cockpit render in THIS order
    // (UnifiedDashboard iterates the list — "Role-Specific Dashboards").
    // attentionInbox consolidates the old temp-alerts banner, overdue-
    // tasks banner, and needs-attention section into one inbox; teamPulse
    // is the demoted engagement grid (activity/posts/recognition).
    dashboardSections: [
      'fleetCommand',
      'locationCockpit',
      'shiftCheckin',
      'attentionInbox',
      'taskList',
      'checklistHub',
      'salesGoals',
      'reviews',
      'coverageBoard',
      'teamPulse',
    ],
    // 2026-07 role-tuned defaults: the franchisor dashboard is pure
    // fleet oversight — setup pill, fleet command (the EOD projection
    // lives in its KPI band now), store drill-down. Store-ops surfaces
    // are one Customize-click away, not deleted. primeCostHero was
    // absorbed into the fleet KPI band ("Projected close" tile).
    defaultHiddenSections: [
      'shiftCheckin',
      'attentionInbox',
      'taskList',
      'checklistHub',
      'salesGoals',
      'reviews',
      'coverageBoard',
      'teamPulse',
    ],
    defaultAlertSubscriptions: [
      'prime_cost_pct',
      'mid_shift_labor_pct',
      'food_cost_pct',
      'labor_pct',
      'net_sales_dip_pct',
    ],
    fullyImplemented: true,
  },
  corporate: {
    id: 'corporate',
    label: 'Company Leadership',
    description:
      'Portfolio view across the locations a corporate user owns — the All-Stores rollup plus per-store drill-down. Sees operational + sales context but no cost metrics — prime / food / labor cost surfaces are admin-only (unless corporate_sees_cost_metrics is enabled).',
    appliesTo: ['corporate'],
    // Cost tiles (operatorKpiTiles) and the cost-alert inbox stay on the
    // admin view only. Corporate keeps the sales tiles and operational
    // surfaces (tasks). See showsCostMetrics() — every cost-displaying
    // component should consult that predicate, not branch on role.
    // The Location Cockpit is the corporate lead surface — the full
    // operational metric set for the selected store (net sales, checks,
    // avg check, dayparts, voids, discounts, channel mix) with no cost
    // dollars. See showsCostMetrics() — SPLH is hours-based (operational),
    // so it stays; prime / food / labor dollars do not.
    //
    // fleetCommand: corporate IS the multi-unit operator, so they get the
    // All-Stores rollup (and the "All locations" scope) — previously
    // admin-only, which left the portfolio persona with a one-store-at-a-time
    // view. The cost columns inside it (labor % / food % / prime %) stay
    // gated by showsCostMetrics(), so the rollup shows sales + operational
    // aggregates only unless corporate_sees_cost_metrics is on.
    dashboardSections: [
      'fleetCommand',
      'locationCockpit',
      'shiftCheckin',
      'attentionInbox',
      'taskList',
      'checklistHub',
      'coverageBoard',
      'salesGoals',
      'reviews',
      'teamPulse',
    ],
    // Weekly-cadence content (reviews, engagement stats) is opt-in on
    // the daily-pulse page.
    defaultHiddenSections: ['reviews', 'teamPulse'],
    // Sales-dip is fine for corporate (top-line signal, not a cost
    // metric); prime / food / labor were dropped per the 2026-06-08
    // cost-visibility tightening.
    defaultAlertSubscriptions: [
      'net_sales_dip_pct',
    ],
    fullyImplemented: true,
  },
  lead_manager: {
    id: 'lead_manager',
    label: 'Lead Manager',
    description:
      'Store Lead / Assistant Store Lead — the corporate operator view locked to their assigned location(s). Full single-store sales + operational surfaces, labor hours only, no cost dollars, no location switching, no fleet view.',
    appliesTo: ['lead_manager'],
    // Mirrors corporate exactly; the single-location restriction is
    // enforced by LocationContext (no canSwitchLocations → assigned
    // user_locations only) and, fail-closed, by auth_location_ids() in
    // the database. showsCostMetrics() stays admin-only, so wage and
    // cost surfaces never render for this role.
    dashboardSections: [
      'locationCockpit',
      'shiftCheckin',
      'attentionInbox',
      'taskList',
      'checklistHub',
      'coverageBoard',
      'salesGoals',
      'reviews',
      'teamPulse',
    ],
    defaultHiddenSections: ['reviews', 'teamPulse'],
    defaultAlertSubscriptions: [
      'net_sales_dip_pct',
    ],
    fullyImplemented: true,
  },
  manager: {
    id: 'manager',
    label: 'Store Leadership',
    description:
      'Single-store operational view — coverage, checklist completion, today\'s tasks. No cost metrics; labor is hours-only at this level.',
    appliesTo: ['manager'],
    // Same cost gate as corporate: no operatorKpiTiles, no cost alerts.
    // Managers see operational surfaces (sales, checklists, tasks) and
    // hours-based labor (scheduled vs actual) but no dollar amounts.
    // Managers lead with what's wrong (attention inbox: temps, overdue,
    // maintenance), then the shift surfaces (checklists, tasks, coverage).
    dashboardSections: [
      'attentionInbox',
      'checklistHub',
      'taskList',
      'coverageBoard',
      'salesGoals',
      'reviews',
      'teamPulse',
    ],
    // A shift view: what's wrong, checklists, tasks, coverage, and the
    // day's sales goal — managers own the number. Reviews / engagement
    // are opt-in.
    defaultHiddenSections: ['reviews', 'teamPulse'],
    defaultAlertSubscriptions: [],
    fullyImplemented: true,
  },
  employee: {
    id: 'employee',
    label: 'Associate',
    description:
      'Personal shift focus: your tasks, open shifts you can pick up, and team recognition. No store-wide analytics or triage — those are supervisory surfaces (checklist completion is a location KPI, the attention inbox is a manager triage queue), so an associate leads with what THEY can act on.',
    appliesTo: ['employee'],
    // Associate view is personal, not supervisory. Deliberately omits
    // checklistHub (store-wide completion analytics — a manager KPI) and
    // attentionInbox (store-wide overdue/flagged triage — a leadership
    // queue; temps were already hidden from associates). teamPulse
    // (recognition/points) is default-VISIBLE here because it's the one
    // engagement surface that's meaningful to a frontline associate.
    dashboardSections: ['taskList', 'coverageBoard', 'teamPulse'],
    defaultHiddenSections: [],
    defaultAlertSubscriptions: [],
    fullyImplemented: true,
  },
};

/** Maps an app_role string (from profiles.role / membership.role) to a
 *  RoleView. Unknown / null roles fall back to the most-restrictive
 *  view so we never accidentally show admin surfaces to a profile
 *  whose role hasn't loaded yet. */
export function getRoleView(role: AppRole | string | null | undefined): RoleView {
  if (!role) return ROLE_VIEWS.employee;
  for (const view of Object.values(ROLE_VIEWS)) {
    if (view.appliesTo.includes(role as AppRole)) return view;
  }
  return ROLE_VIEWS.employee;
}

/** Convenience predicates — keep components from re-deriving
 *  section membership on every render. */
export function hasSection(
  role: AppRole | string | null | undefined,
  section: DashboardSection,
): boolean {
  return getRoleView(role).dashboardSections.includes(section);
}

/** PR A's headline gate: prime cost hero is admin/super_admin only
 *  until the franchisee/manager variants land in a follow-up PR. */
export function showsPrimeCostHero(role: AppRole | string | null | undefined): boolean {
  return hasSection(role, 'primeCostHero');
}

/** Single-location operations cockpit — corporate (lead surface) and
 *  admin (the store drill-down target from the fleet view). */
export function showsLocationCockpit(role: AppRole | string | null | undefined): boolean {
  return hasSection(role, 'locationCockpit');
}

/** All-Stores Command fleet view — admin / super_admin only. */
export function showsFleetCommand(role: AppRole | string | null | undefined): boolean {
  return hasSection(role, 'fleetCommand');
}

/** Per-company loosenings of the cost-visibility gates below. Populated
 *  from company_policy_settings (useCompanyPolicies) at React call
 *  sites; pure/non-component callers omit it and keep the strict
 *  admin-only default. */
export interface CostVisibilityOpts {
  /** True when the company enabled the corporate_sees_cost_metrics
   *  policy (Settings → Team permissions) — opens cost & labor dollars
   *  to the 'corporate' role for that company only. */
  corporateCostVisible?: boolean;
}

/** Catch-all cost-visibility gate (2026-06-08).
 *
 *  Prime cost, food cost, labor cost / labor % are admin + super_admin
 *  only — corporate, manager, and employee see operational data (hours,
 *  sales, tasks) but never dollar amounts or % of sales for the cost
 *  side. Every cost-displaying component (dashboard tiles, alert lists,
 *  schedule labor panels, manager mobile labor tile, analytics labor
 *  KPIs, etc.) should consult this predicate so a single edit here
 *  re-opens the data to other roles without sweeping the codebase
 *  again. Hours-only labor surfaces (e.g., scheduled vs actual hours)
 *  are NOT gated by this — those are operational, not cost.
 *
 *  2026-07 per-company policies: pass
 *  `{ corporateCostVisible: hasPolicy('corporate_sees_cost_metrics') }`
 *  from useCompanyPolicies to additionally open the gate to the
 *  'corporate' role when the company has switched that policy on. */
export function showsCostMetrics(
  role: AppRole | string | null | undefined,
  opts?: CostVisibilityOpts,
): boolean {
  return (
    role === 'admin'
    || role === 'super_admin'
    || (role === 'corporate' && opts?.corporateCostVisible === true)
  );
}

/** OT / labor-hours / timesheets visibility (2026-06-08, sibling to
 *  showsCostMetrics). Same admin/super_admin gate, broader scope:
 *  overtime hours, hours-worked totals, the team timesheets surface,
 *  the "x employees over 40h" alert tile — anything that exposes
 *  payroll-shaped labor data. Kept as a separate predicate so a
 *  future loosening of one of the two categories is a one-line
 *  change here, not a sweep through the components. Accepts the same
 *  per-company opts as showsCostMetrics (the corporate_sees_cost_metrics
 *  policy covers both cost and labor dollars). */
export function showsLaborMetrics(
  role: AppRole | string | null | undefined,
  opts?: CostVisibilityOpts,
): boolean {
  return showsCostMetrics(role, opts);
}
